/* eslint-disable no-console */
/* ========================================================================== *\
	IMPORTS
\* ========================================================================== */
import bind from 'lodash/bind';
import debounce from 'lodash/debounce';
import isDate from 'lodash/isDate';
import isElement from 'lodash/isElement';
import isFunction from 'lodash/isFunction';
import isNil from 'lodash/isNil';

import {
	isInRange,
	removeTime,
	isSameMonthAndYear,
	addMonths
} from 'js-date-helper';

import { getCalendarMonthForDate } from '../../01-helpers/utilities/date.js';
import { generateDateMetadata } from '../../01-helpers/utilities/date-metadata.js';
import DateRange from '../../01-helpers/utilities/date-range.js';

import DateNavigator from '../navigator/navigator.date.js';



/* ========================================================================== *\
	TYPE DEFINITIONS
\* ========================================================================== */

/**
 * @typedef {Object} CalendarOptions
 *
 * @property {Date} [currentDate]
 * @property {Date} [maxDate]
 * @property {Date} [minDate]
 * @property {Function} [monthRenderer]
 * @property {number} [monthSpan]
 * @property {Function} [headerRenderer]
 * @property {boolean} [renderOnInit=true]
 * @property {Date} [selectionEnd]
 * @property {Date} [selectionStart]
 * @property {boolean} sixWeeks
 */

/**
 * @callback renderMethod
 *
 * @param {Array<DateMetadata>} days An array with days which should be rendered.
 * @param {Symbol} reason The reason/trigger to render the days.
 *
 * @returns {Promise<string>}
 */

/**
 * @callback renderHeaderMethod
 *
 * @param {Date[]} months An array containing the first day of the months to be
 *        displayed in the calendar.
 *
 * @returns {string} A formatted date to be displayed in the header.
 */

/**
 * @typedef { import('../01-utilities/date-metadata').DateMetadata } BaseDateMetadata
 */

/**
 * @typedef {Object} AdditionalMetaData
 * @property {boolean} isPadding True when the date falls in a different month
 *           than the month the calendar is showing; otherwise false.
 * @property {boolean} isSelected The selected day, or days belonging to a
 *           range of selected days will have this property set to true.
 * @property {boolean} isSelectionStart The date which is the start of the range
 *           will have this property set to true.
 * @property {boolean} isSelectionEnd The date which is the end of the range
 *           will have this property set to true.
 */

/**
 * @typedef {BaseDateMetadata & AdditionalMetaData} DateMetadata
 */

/**
 * @typedef {import('../01-utilities/date-range.js').DateRangeObject} DateRangeObject
 */

/* ========================================================================== *\
	PRIVATE VARIABLES
\* ========================================================================== */

const
	moduleName = 'calendar',
	baseJsClass = `js-${ moduleName }`,

	cssClass = {
		isNavigating: 'is-navigating'
	},

	propertyKey = {
		baseElement: Symbol('baseElement'),
		date: Symbol('date'),
		debouncedRefresh: Symbol('debouncedRefresh'),
		firstWeekDay: Symbol('firstWeekDay'),
		monthSpan: Symbol('monthSpan'),
		navigator: Symbol('navigator'),
		rangeSelection: Symbol('rangeSelection'),
		renderDays: Symbol('renderDays'),
		renderHeader: Symbol('renderHeader'),
		sixWeeks: Symbol('sixWeeks')
	},

	renderReason = Object.freeze({
		/**
		 * When the user was navigating and stopped for a small amount of time
		 * the calender will try to refresh and passes navigationFinished as
		 * a reason.
		 */
		navigationFinished: Symbol('navigationFinished'),
		navigated: Symbol('navigated'),
		rendererChanged: Symbol('rendererChanged')
	}),

	selector = {
		base: `.${ baseJsClass }`,
		container: `.${ baseJsClass}-container`,
		headerLabel: `.${ baseJsClass }-label`,
		navigator: `.${ baseJsClass }-navigator`
	};

let
	counter = 0,
	globalRenderNonce = null;


/* ========================================================================== *\
	EVENT HANDLING
\* ========================================================================== */

/**
 *
 * @param {Event} event
 *
 * @this Calendar
 */
function onNavigationFinished(event) {
	refreshDays.call(this, renderReason.navigationFinished).then(isRendered => {
		if (isRendered) {
			this.baseElement.classList.remove(cssClass.isNavigating);
		}
	});
}

/**
 *
 * @param {Event} event
 *
 * @this Calendar
 */
function onNavigationStart(event) {
	this.baseElement.classList.add(cssClass.isNavigating);
}

/**
 *
 * @param {Event} event
 *
 * @this Calendar
 */
function onNavigated(event) {
	refresh.call(this, renderReason.navigated);
}

/**
 *
 */
function onSelectionChanged() {
	this.refreshCalendar();
}

/**
 * @param {DateRangeObject} range
 *
 * @this Calendar
*/
function onSelectionWillChange(range) {
	// Check if the new selection is within the navigation range.
	return this.navigationRange.containsSubrange(range);
}



/* ========================================================================== *\
	PRIVATE METHODS
\* ========================================================================== */

/**
 *
 * @param {Array<DateMetadata>} days
 * @param {DateRangeObject} range
 */
function addSelectedRangeMetadata(days, { start, end }) {
	const
		selectedDays = (isNil(end) || isNil(start))
			? []
			: days.filter(day => isInRange(day.timestamp, start, end));

	selectedDays.forEach(day => {
		day.isSelected = true;
		day.isSelectionEnd = (day.timestamp === end.getTime());
		day.isSelectionStart = (day.timestamp === start.getTime());
	});
}

/**
 *
 * @param {Array<DateMetadata>} days
 * @param {Date} referenceDate
 */
function addPaddingMetadata(days, referenceDate) {
	days.forEach(day => {
		day.isPadding = !isSameMonthAndYear(day.date, referenceDate);
	});
}

/**
 * A fallback renderer for rendering the current month shown in the calendar.
 *
 * @param {Date} date The date for the month shown in the calendar.
 *
 * @returns {string} A formatted date to be displayed in the header.
 */
function fallbackHeaderRenderer(date) {
	console.warn('No render method for the header label  has been set.');

	return `${ (date.getMonth() + 1).toString().padStart(2, '0') }-${ date.getFullYear() }`
}

/**
 * A fallback renderer for rendering a month. It will be used when no month
 * renderer has been set.
 *
 * @param {Array<DateMetadata>} days An array with DateMetadata objects for the
 *        days to render.
 *
 * @returns {Promise<string>} A promise to return an HTML string which will
 *          contain div elements to show all the provided days.
 */
function fallbackMonthRenderer(days) {
	console.warn('No render method for the month has been set.');

	const
		htmlString = days.reduce((result, day) => {
			return result + `<div>${day.dateParts.date}</div>`
		}, '');

	return Promise.resolve(htmlString);
}

/**
 * Generates an array with the first day of the month for the months currently
 * shown in the calendar.
 *
 * @returns {Array<DateMetadata[]} An array with the first day of the month of
 *          the months currently shown in the calendar.
 *
 * @this Calendar
 */
function getDisplayedMonths() {
	const
		date = this[propertyKey.navigator].currentDate,
		result = [];

	for (let delta = 0; delta < this.monthSpan; delta++) {
		result.push(addMonths(date, delta));
	}

	return result;
}

/**
 * @returns {Array<DateMetadata[]}
 *
 * @this Calendar
 */
function generateRenderInput() {
	return getDisplayedMonths.call(this).map(date => {
		return getCalendarMonthForDate(date, this.firstWeekDay, this.sixWeeks).map(generateDateMetadata)
	});
}

/**
 *
 * @param {string} name
 */
function isNameANavigatorEvent(name) {
	return Object.keys(DateNavigator.events).includes(name);
}

/**
 * @param {Array<DateMetadata[]>} days
 *
 * @this Calendar
 */
function preprocessDays(months) {
	let delta = 0;
	months.forEach(month => {
		addSelectedRangeMetadata(month, this.selection.range);
		addPaddingMetadata(month, addMonths(this[propertyKey.navigator].currentDate, delta++));
	});

	// Allow sub components to add their metadata to the days about to
	// be rendered.
	this._preprocessDays(months);
}

/**
 *
 * @param {renderReason} reason
 *
 * @this Calendar
 */
function refresh(reason) {
	refreshHeader.call(this);
	refreshDays.call(this, reason);
}

/**
 * @this Calendar
 */
function refreshHeader() {
	const
		label = this.baseElement.querySelector(selector.headerLabel);
	if (label === null) {
		return;
	}

	label.innerHTML = this.renderHeader(getDisplayedMonths.call(this));
}

/**
 * @returns Promise<boolean> Returns a promise which is fulfilled once the
 *          refresh has been completed. The return value will be false when the
 *          refresh did not result in the view getting updated.
 *
 * @this Calendar
 */
async function refreshDays(reason) {
	const
		localNonce = globalRenderNonce = Symbol(++counter),
		container = this.baseElement.querySelector(selector.container);
	if (container === null) {
		return Promise.resolve();
	}

	const
		renderInput = generateRenderInput.call(this, this[propertyKey.navigator].currentDate);

	preprocessDays.call(this, renderInput);
	const renderResult = await this.renderDays(renderInput, reason);
	// Make sure this refresh is still the most recent refresh. When it is not
	// the most recent, ignore the result as a later refresh is now responsible
	// for updating the view.
	if (localNonce !== globalRenderNonce) {
		return false;
	}

	container.innerHTML = renderResult;
	
	return true;
}

/* ========================================================================== *\
	SETUP
\* ========================================================================== */
/**
 *
 * @param {CalendarOptions} options
 *
 * @this Calendar
 */
function setup(options) {
	// The navigator must be setup before the range selection due to range
	// selection being dependent on the min/max navigation range.
	this[propertyKey.navigator] = setupNavigator.call(this, options);
	this[propertyKey.rangeSelection] = setupSelectionRange.call(this, options);
	setupProperties.call(this, options);
}

/**
 * @returns {GenericNavigator}
 *
 * @this Calendar
 */
function setupNavigator(options) {
	const
		instance = new DateNavigator(this.baseElement);

	instance.navigationRange.updateRange(options.minDate, options.maxDate);
	instance.currentDate = isDate(options.currentDate) ? options.currentDate : new Date();

	instance.register(DateNavigator.events.navigationFinished, bind(onNavigationFinished, this));
	instance.register(DateNavigator.events.navigationStart, bind(onNavigationStart, this));
	instance.register(DateNavigator.events.navigated, bind(onNavigated, this));

	return instance;
}

/**
 *
 * @param {CalendarOptions} options
 *
 * @this Calendar
 */
function setupProperties(options) {
	this[propertyKey.firstWeekDay] = 1;

	this[propertyKey.monthSpan] = (Number.isFinite(options.monthSpan) && options.monthSpan >= 1)
		? options.monthSpan
		: 1;

	this[propertyKey.renderHeader] = isFunction(options.headerRenderer)
		? options.headerRenderer
		: null;

	this[propertyKey.renderDays] = isFunction(options.monthRenderer)
		? options.monthRenderer
		: null;

	this[propertyKey.sixWeeks] =!!options.sixWeeks;
}

/**
 *
 * @param {CalendarOptions} options
 *
 * @this Calendar
 */
function setupSelectionRange(options) {
	/**
	 * @type {import('../01-utilities/date-range.js').DateRangeOptions}
	 */
	const
		dateRangeOptions = {
			hasChangedCallback: bind(onSelectionChanged, this),
			willChangeCallback: bind(onSelectionWillChange, this)
		},
		end = isDate(options.selectionEnd) && this.navigationRange.isDateInRange(options.selectionEnd)
			? removeTime(options.selectionEnd)
			: null,
		start = isDate(options.selectionStart) && this.navigationRange.isDateInRange(options.selectionStart)
			? removeTime(options.selectionStart)
			: null,
		initialRange = {
			end,
			start
		};

	return new DateRange(dateRangeOptions, initialRange);
}



/* ========================================================================== *\
	PUBLIC API
\* ========================================================================== */

class Calendar {
	/* ====================================================================== *\
		CONSTRUCTOR
	\* ====================================================================== */
	/**
	 *
	 * @param {HTMLElement} baseElement
	 * @param {CalendarOptions} [options={}]
	 */
	constructor(baseElement, options = {}) {
		if (!isElement(baseElement)) {
			throw 'Unable to create instance of Calendar, no base element has been provided.';
		}

		this[propertyKey.debouncedRefresh] = debounce(bind(refresh, this), 100);
		this[propertyKey.baseElement] = baseElement;
		setup.call(this, options);

		if (options.renderOnInit) {
			this[propertyKey.debouncedRefresh](renderReason.rendererChanged);
		}
	}



	/* ====================================================================== *\
		STATIC PROPERTIES
	\* ====================================================================== */
	/**
	 * The possible reasons why the calender is requesting a rendering of the
	 * dates in the view.
	 *
	 * @readonly
	 * @static
	 * @memberof Calendar
	 */
	static get reason() {
		return renderReason;
	}



	/* ====================================================================== *\
		INSTANCE PROPERTIES
	\* ====================================================================== */

	/* ---------------------------------- *\
		baseElement (r/o)
	\* ---------------------------------- */
	/**
	 * Returns the element which was used to create an instance of Navigator.
	 *
	 * @type {HTMLElement}
	 *
	 * @readonly
	 * @memberof Calendar
	 */
	get baseElement() {
		return this[propertyKey.baseElement];
	}

	/* ---------------------------------- *\
		firstWeekDay
	\* ---------------------------------- */
	/**
	 * This first day of the week. Setting this property will cause the calendar
	 * to be refreshed.
	 *
	 * @type {Number}
	 *
	 * @memberof Calendar
	 */
	get firstWeekDay() {
		return this[propertyKey.firstWeekDay];
	}
	set firstWeekDay(value) {
		if (
			!Number.isFinite(value) ||
			value < 0 ||
			value > 6
		) {
			return;
		}

		this[propertyKey.firstWeekDay] = value;
		this[propertyKey.debouncedRefresh](renderReason.rendererChanged);
	}

	/* ---------------------------------- *\
		monthSpan
	\* ---------------------------------- */
	/**
	 * The number of months to show in the calendar. Setting this property will
	 * cause the calendar to be refreshed.
	 *
	 * @type {number}
	 *
	 * @memberof Calendar
	 */
	get monthSpan() {
		return this[propertyKey.monthSpan];
	}
	set monthSpan(value) {
		if (
			!Number.isFinite(value) ||
			value < 1
		) {
			return;
		}

		this[propertyKey.monthSpan] = value;
		this[propertyKey.debouncedRefresh](renderReason.rendererChanged);
	}

	/* ---------------------------------- *\
		navigationRange
	\* ---------------------------------- */
	/**
	 * @type {DateRange}
	 *
	 * @readonly
	 * @memberof Calendar
	 */
	get navigationRange() {
		return this[propertyKey.navigator].navigationRange;
	}

	/* ---------------------------------- *\
		renderDays
	\* ---------------------------------- */
	/**
	 * This property is to set the method to use to render the days in the
	 * calendar. Setting this property will cause the calendar to be refreshed.
	 *
	 * @type {renderMethod}
	 *
	 * @memberof Calendar
	 */
	get renderDays() {
		return isNil(this[propertyKey.renderDays])
			? fallbackMonthRenderer
			: this[propertyKey.renderDays];
	}
	set renderDays(method) {
		if (!isFunction(method)) {
			return;
		}

		this[propertyKey.renderDays] = method;
		this[propertyKey.debouncedRefresh](renderReason.rendererChanged);
	}

	/* ---------------------------------- *\
		renderHeader
	\* ---------------------------------- */
	/**
	 * Setting this property will cause the calendar to be refreshed.
	 * @type {renderHeaderMethod}
	 *
	 * @memberof Calendar
	 */
	get renderHeader() {
		return (isNil(this[propertyKey.renderHeader]))
			? fallbackHeaderRenderer
			: this[propertyKey.renderHeader];
	}
	set renderHeader(method) {
		if (!isFunction(method)) {
			return;
		}

		this[propertyKey.renderHeader] = method;
		refreshHeader.call(this);
	}

	/* ---------------------------------- *\
		selection
	\* ---------------------------------- */
	/**
	 * @type {DateRange}
	 *
	 * @readonly
	 * @memberof Calendar
	 */
	get selection() {
		return this[propertyKey.rangeSelection];
	}


	/* ---------------------------------- *\
		sixWeeks
	\* ---------------------------------- */
	/**
	 * Setting this property to true will ensure each month will be rendered
	 * with 6 weeks, the maximum number of calendar weeks a month can need to be
	 * fully displayed.
	 *
	 * @type {boolean}
	 *
	 * @memberof Calendar
	 */
	get sixWeeks() {
		return this[propertyKey.sixWeeks];
	}
	set sixWeeks(value) {
		if (value === this.sixWeeks) {
			return;
		}

		this[propertyKey.sixWeeks] = !!value;
		this[propertyKey.debouncedRefresh](renderReason.rendererChanged);
	}



	/* ====================================================================== *\
		CALLBACK REGISTRATION
	\* ====================================================================== */
	register(name, callback) {
		if (isNameANavigatorEvent(name)) {
			this[propertyKey.navigator].register(DateNavigator.events[name], callback);
		}
	}

	unregister(name, callback) {
		if (isNameANavigatorEvent(name)) {
			this[propertyKey.navigator].unregister(name, callback);
		}
	}



	/* ====================================================================== *\
		PROTECTED METHODS
	\* ====================================================================== */
	/**
	 *
	 * @param {Array<DateMetadata[]>} days
	 *
	 * @virtual
	 * @protected
	 */
	_preprocessDays(days) {
		//
	}



	/* ====================================================================== *\
		PUBLIC METHODS
	\* ====================================================================== */
	refreshCalendar() {
		this[propertyKey.debouncedRefresh](renderReason.rendererChanged);
	}
}



/* ========================================================================== *\
	EXPORTS
\* ========================================================================== */
export default Calendar;
